import { ChangeDetectionStrategy, Component, computed, input, output, ViewEncapsulation } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { SplitButtonModule } from 'primeng/splitbutton';

function optionalIconClasses(value: string | undefined): string | undefined {
    const trimmed = value?.trim();
    return trimmed ? trimmed : undefined;
}

@Component({
    selector: 'fcp-splitbutton',
    standalone: true,
    imports: [SplitButtonModule],
    styleUrl: './fcp-splitbutton.component.less',
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '[attr.data-e2e-hook]': 'e2e()'
    },
    template: `<p-splitbutton
        [model]="model()"
        [severity]="severity()"
        [rounded]="rounded()"
        [text]="text()"
        [outlined]="outlined()"
        [size]="splitButtonSize()"
        [icon]="displayIcon()"
        iconPos="left"
        [label]="displayLabel()"
        [buttonProps]="mainButtonProps()"
        [styleClass]="styleClass()"
        [menuStyle]="menuStyle()"
        [menuStyleClass]="menuStyleClass()"
        [dropdownIcon]="splitButtonDropdownIcon"
        [appendTo]="appendTo()"
        [dir]="dir()"
        [expandAriaLabel]="expandAriaLabel()"
        [showTransitionOptions]="showTransitionOptions()"
        [hideTransitionOptions]="hideTransitionOptions()"
        [disabled]="disabled()"
        [tabindex]="tabindex()"
        [menuButtonDisabled]="disabled()"
        [buttonDisabled]="disabled()"
        (onClick)="onClick.emit($event)"
        (onMenuHide)="onMenuHide.emit($event)"
        (onMenuShow)="onMenuShow.emit($event)"
        (onDropdownClick)="onDropdownClick.emit($event)"
    />`
})
export class FcpSplitButtonComponent {
    readonly splitButtonDropdownIcon =
        'fc-font-icon fc-font-icon-chevron-filled-down p-button-icon fcp-splitbutton-dropdown-icon';

    e2e = input<string | null>(null);
    model = input<MenuItem[]>();
    severity = input<SplitButtonSeverity>();
    rounded = input<boolean>(false);
    splitButtonVariant = input<SplitButtonVariant>(SplitButtonVariant.Default);
    splitButtonSize = input<SplitButtonSize>(SplitButtonSize.Normal);
    icon = input<string | undefined>(undefined);
    iconOnly = input<boolean>(false);
    label = input<string>();
    ariaLabel = input<string | undefined>(undefined);
    styleClass = input<string>();
    menuStyle = input<unknown>();
    menuStyleClass = input<string>();
    appendTo = input<unknown>('body');
    dir = input<string>();
    expandAriaLabel = input<string>();
    showTransitionOptions = input<string>();
    hideTransitionOptions = input<string>();
    disabled = input<boolean>(false);
    tabindex = input<number>();

    onClick = output<MouseEvent>();
    onMenuHide = output<unknown>();
    onMenuShow = output<unknown>();
    onDropdownClick = output<MouseEvent>();

    outlined = computed(() => this.splitButtonVariant() === SplitButtonVariant.Outlined);
    text = computed(() => this.splitButtonVariant() === SplitButtonVariant.Text);

    displayIcon = computed(() => optionalIconClasses(this.icon()));

    displayLabel = computed(() => {
        const hideLabelForIconOnly = this.iconOnly() && this.displayIcon() !== undefined;
        return hideLabelForIconOnly ? undefined : this.label();
    });

    mainButtonProps = computed(() => {
        const aria = this.ariaLabel();
        return aria ? { ariaLabel: aria } : undefined;
    });
}
export enum SplitButtonSeverity {
    Primary = 'primary',
    Secondary = 'secondary',
    Tertiary = 'help'
}

export enum SplitButtonVariant {
    Default = null,
    Outlined = 'outlined',
    Text = 'text'
}

export enum SplitButtonSize {
    Large = 'large',
    Normal = null
}
